import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { parse, format, isValid } from 'date-fns';
import { FaTimes, FaPlus } from 'react-icons/fa';
import { theme, t } from '../theme';
import { toast } from 'react-toastify';
import { norm } from '../utils';
import { casarPorCodigo, escanearVarios, disponivel as scannerDisponivel } from '../platform/scanner';
import BotaoScanner from './BotaoScanner';

const StockEntry = ({ produtoSelecionado = null, fecharModal = null, isModal = false }) => {
    const [produtos, setProdutos] = useState([]);
    const [filteredProdutos, setFilteredProdutos] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const navigate = useNavigate();
    // Fila do modo "escanear vários": a usuaria le a compra toda de uma vez e
    // depois o formulario passa item por item, ja com o produto selecionado.
    // Reaproveita este form em vez de criar uma tela de carrinho com todos os
    // campos (lote, validade, custo) repetidos por linha.
    const [fila, setFila] = useState([]);          // produtos casados, na ordem lida
    const [filaIdx, setFilaIdx] = useState(0);
    const [naoAchados, setNaoAchados] = useState([]);
    const [estoqueAtual, setEstoqueAtual] = useState(0);
    const [produtoInfo, setProdutoInfo] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [campoValidade, setCampoValidade] = useState('');
    const [validadeError, setValidadeError] = useState('');
    const [precoFormatado, setPrecoFormatado] = useState('');
    const [pesoFormatado, setPesoFormatado] = useState('');
    const [quantidadeFormatada, setQuantidadeFormatada] = useState('');
    const [salvando, setSalvando] = useState(false);
    const modalRef = useRef(null);

    const [entrada, setEntrada] = useState({
        produto_id: '', codigo_barras: '', nome_produto: '',
        numero_lote: '', preco_custo: '', validade: '',
        quantidade: '', peso_embalagem: '',
    });

    useEffect(() => {
        api.get('/produtos').then(r => setProdutos(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (produtoSelecionado) {
            setEntrada(prev => ({ ...prev, produto_id: produtoSelecionado.id, nome_produto: produtoSelecionado.nome, codigo_barras: produtoSelecionado.codigo_barras }));
            setProdutoInfo(produtoSelecionado);
            setEstoqueAtual(produtoSelecionado.estoque_atual || 0);
            setShowDropdown(false);
            setPrecoFormatado(''); setPesoFormatado(''); setQuantidadeFormatada('');
        }
    }, [produtoSelecionado]);

    useEffect(() => {
        if (!isModal) return;
        const handler = (e) => { if (modalRef.current && !modalRef.current.contains(e.target)) fecharModal?.(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isModal, fecharModal]);

    const handleNomeChange = (e) => {
        const v = e.target.value;
        setEntrada(prev => ({ ...prev, nome_produto: v }));
        if (v.length > 0) { setFilteredProdutos(produtos.filter(p => norm(p.nome).includes(norm(v)))); setShowDropdown(true); }
        else { setShowDropdown(false); resetCamposProduto(); }
    };

    const handleCodigoBarrasChange = (e) => {
        const v = e.target.value;
        setEntrada(prev => ({ ...prev, codigo_barras: v }));
        if (v.length > 0) { setFilteredProdutos(produtos.filter(p => p.codigo_barras?.includes(v) || p.codigo_barras_alternativo?.includes(v))); setShowDropdown(true); }
        else { setShowDropdown(false); resetCamposProduto(); }
    };

    // Codigo vindo da camera. Diferente da digitacao: aqui o codigo esta
    // COMPLETO, entao casamos por igualdade — `includes` selecionaria o produto
    // errado quando um EAN e prefixo de outro.
    const escanearSequencia = async () => {
        const achados = [];
        const faltantes = [];
        const r = await escanearVarios((codigo) => {
            const m = casarPorCodigo(produtos, codigo);
            if (m.length === 1) achados.push(m[0]);
            else if (m.length === 0) faltantes.push(codigo);
            // Ambiguo (mesmo codigo em dois produtos) fica de fora: resolver
            // no meio de uma sequencia de leituras atrapalha mais que ajuda.
        });

        if (r.motivo === 'sem-permissao') {
            toast.warn('Libere o acesso à câmera nas configurações do aparelho.');
            return;
        }
        if (!achados.length && !faltantes.length) return;   // desistiu sem ler nada

        setNaoAchados(faltantes);
        if (achados.length) {
            setFila(achados);
            setFilaIdx(0);
            handleProdutoSelect(achados[0]);
            toast.success(`${achados.length} ${achados.length === 1 ? 'item lido' : 'itens lidos'}. Preencha um por um.`);
        }
        if (faltantes.length) {
            toast.info(`${faltantes.length} código(s) não cadastrado(s) — vou lembrar você no fim.`);
        }
    };

    const handleCodigoEscaneado = (codigo) => {
        setEntrada(prev => ({ ...prev, codigo_barras: codigo }));
        const achados = casarPorCodigo(produtos, codigo);

        if (achados.length === 1) {
            handleProdutoSelect(achados[0]);
            return;
        }
        if (achados.length > 1) {
            setFilteredProdutos(achados);
            setShowDropdown(true);
            return;
        }
        setFilteredProdutos([]);
        setShowDropdown(false);
        // Nao basta avisar: leva pro cadastro com o codigo preenchido. Digitar
        // 13 digitos a mao e onde a usuaria desiste.
        toast.info(
            ({ closeToast }) => (
                <div>
                    <div style={{ marginBottom: 8 }}>
                        Código <strong>{codigo}</strong> não está cadastrado.
                    </div>
                    <button
                        type="button"
                        onClick={() => { closeToast(); navigate(`/produtos?codigo=${encodeURIComponent(codigo)}`); }}
                        style={{ ...t.btnPrimary, padding: '6px 12px', fontSize: 13 }}
                    >
                        Cadastrar este produto
                    </button>
                </div>
            ),
            { autoClose: 8000 }
        );
    };

    const handleProdutoSelect = (produto) => {
        setEntrada(prev => ({ ...prev, produto_id: produto.id, nome_produto: produto.nome, codigo_barras: produto.codigo_barras }));
        setProdutoInfo(produto); setEstoqueAtual(produto.estoque_atual || 0); setShowDropdown(false);
    };

    const resetCamposProduto = () => {
        setEntrada(prev => ({ ...prev, produto_id: '', nome_produto: '', codigo_barras: '' }));
        setProdutoInfo(null); setEstoqueAtual(0);
    };

    const handleValidadeChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        let formatado = '';
        if (raw.length <= 2) formatado = raw;
        else if (raw.length <= 4) formatado = `${raw.slice(0, 2)}/${raw.slice(2)}`;
        else if (raw.length <= 6) formatado = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
        else formatado = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4, 8)}`;
        setCampoValidade(formatado);

        // Aceitar DD/MM/AA (6 dígitos) ou DD/MM/AAAA (8 dígitos)
        if (raw.length === 6 || raw.length === 8) {
            const anoStr = raw.length === 6 ? `20${raw.slice(4)}` : raw.slice(4);
            const tentativa = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${anoStr}`;
            const parsed = parse(tentativa, 'dd/MM/yyyy', new Date());
            if (isValid(parsed)) {
                const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
                if (parsed < hoje) { setValidadeError('Data anterior a hoje.'); setEntrada(prev => ({ ...prev, validade: '' })); }
                else { setValidadeError(''); setEntrada(prev => ({ ...prev, validade: format(parsed, 'yyyy-MM-dd') })); }
            } else { setValidadeError('Data inválida.'); setEntrada(prev => ({ ...prev, validade: '' })); }
        } else { setValidadeError(''); }
    };

    const handlePrecoChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (!raw) { setPrecoFormatado(''); setEntrada(prev => ({ ...prev, preco_custo: '' })); return; }
        const valor = parseInt(raw, 10) / 100;
        setPrecoFormatado(valor.toFixed(2).replace('.', ','));
        setEntrada(prev => ({ ...prev, preco_custo: valor }));
    };

    const handlePesoChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (!raw) { setPesoFormatado(''); setEntrada(prev => ({ ...prev, peso_embalagem: '' })); return; }
        const valor = parseInt(raw, 10) / 1000;
        setPesoFormatado(valor.toFixed(3).replace('.', ','));
        setEntrada(prev => ({ ...prev, peso_embalagem: valor }));
    };

    const handleQuantidadeChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        setQuantidadeFormatada(raw);
        setEntrada(prev => ({ ...prev, quantidade: raw ? parseInt(raw) : '' }));
    };

    const formatarUnidade = (u) => ({ kg: 'kg', g: 'g', l: 'l', ml: 'ml', unidade: 'un', caixa: 'cx' }[u?.toLowerCase()] || u);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!entrada.produto_id) { setErrorMessage('Selecione um produto válido.'); return; }
        if (!entrada.peso_embalagem || parseFloat(entrada.peso_embalagem) <= 0) { setErrorMessage('Informe o peso da embalagem.'); return; }
        if (!entrada.validade) { setErrorMessage('Informe uma data de validade válida.'); return; }

        setSalvando(true);
        try {
            await api.post('/movimentacoes', { ...entrada, tipo: 'entrada', peso_embalagem: parseFloat(entrada.peso_embalagem), quantidade: parseInt(entrada.quantidade) });

            // Modo "escanear vários": avanca para o proximo item lido em vez de
            // fechar. So fecha quando a fila acabar.
            const proximo = filaIdx + 1;
            if (fila.length && proximo < fila.length) {
                setFilaIdx(proximo);
                handleProdutoSelect(fila[proximo]);
                // Limpa apenas o que e por-lote; produto vem do proximo da fila.
                setEntrada(prev => ({ ...prev, numero_lote: '', quantidade: '', validade: '', preco_custo: '' }));
                setQuantidadeFormatada(''); setPesoFormatado(''); setPrecoFormatado('');
                setCampoValidade(''); setValidadeError('');
                toast.success(`Item ${filaIdx + 1} de ${fila.length} registrado.`);
                return;
            }

            if (fila.length) {
                toast.success(`Todos os ${fila.length} itens registrados. 🎉`);
                if (naoAchados.length) {
                    toast.info(
                        ({ closeToast }) => (
                            <div>
                                <div style={{ marginBottom: 8 }}>
                                    {naoAchados.length} código(s) lido(s) não estão cadastrados.
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { closeToast(); navigate(`/produtos?codigo=${encodeURIComponent(naoAchados[0])}`); }}
                                    style={{ ...t.btnPrimary, padding: '6px 12px', fontSize: 13 }}
                                >
                                    Cadastrar o primeiro
                                </button>
                            </div>
                        ),
                        { autoClose: 10000 }
                    );
                }
            }
            if (fecharModal) fecharModal();
        } catch (error) {
            setErrorMessage(error.response?.data?.erro || 'Erro ao registrar entrada.');
        } finally { setSalvando(false); }
    };

    const renderFormulario = () => (
        <>
            {fila.length > 1 && (
                <div style={{ ...t.section, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 6, borderRadius: 6, background: theme.colors.bgSection, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(filaIdx / fila.length) * 100}%`,
                            background: theme.colors.success, transition: 'width .3s' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary, whiteSpace: 'nowrap' }}>
                        Item {filaIdx + 1} de {fila.length}
                    </span>
                </div>
            )}
            {errorMessage && <div style={t.error}>{errorMessage}</div>}
            <form onSubmit={handleSubmit}>
                {!produtoSelecionado && (
                    <>
                        <div style={{ marginBottom: 14 }}>
                            <label style={t.label}>Produto</label>
                            <div style={{ position: 'relative' }}>
                                <input value={entrada.nome_produto} onChange={handleNomeChange} placeholder="Buscar por nome..." style={t.input} autoComplete="off" required />
                                {showDropdown && filteredProdutos.length > 0 && (
                                    <ul style={{ position: 'absolute', width: '100%', background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 10, boxShadow: theme.shadow.md, marginTop: 4, zIndex: 20, maxHeight: 160, overflow: 'auto', listStyle: 'none', padding: 0 }}>
                                        {filteredProdutos.map(p => (
                                            <li key={p.id} onClick={() => handleProdutoSelect(p)} style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 14, borderBottom: `1px solid ${theme.colors.borderLight}` }}
                                                onMouseEnter={ev => ev.currentTarget.style.background = theme.colors.bgSection} onMouseLeave={ev => ev.currentTarget.style.background = '#fff'}>
                                                <div style={{ fontWeight: 500 }}>{p.nome}</div>
                                                <div style={{ fontSize: 12, color: theme.colors.textMuted }}>{p.codigo_barras}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={t.label}>Código de Barras</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input value={entrada.codigo_barras} onChange={handleCodigoBarrasChange} placeholder="Código..." style={{ ...t.input, flex: 1 }} autoComplete="off" />
                                <BotaoScanner onLido={handleCodigoEscaneado} />
                                {scannerDisponivel() && (
                                    <button type="button" onClick={escanearSequencia}
                                        title="Escanear vários itens da compra em sequência"
                                        style={{ flexShrink: 0, height: 44, padding: '0 12px', borderRadius: 10,
                                            border: `1px solid ${theme.colors.border}`, background: '#fff',
                                            cursor: 'pointer', fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>
                                        Vários
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {produtoInfo && (
                    <div style={{ ...t.section, marginBottom: 14, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: theme.colors.textSecondary }}>Estoque atual: <strong style={{ color: theme.colors.text }}>{estoqueAtual}</strong></span>
                        {entrada.quantidade > 0 && (
                            <span style={{ color: theme.colors.success }}>Após entrada: <strong>{estoqueAtual + (parseInt(entrada.quantidade) || 0)}</strong></span>
                        )}
                        {!entrada.quantidade && <span style={{ color: theme.colors.textSecondary }}>Unidade: <strong style={{ color: theme.colors.text }}>{formatarUnidade(produtoInfo.unidade_peso)}</strong></span>}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ marginBottom: 14 }}>
                        <label style={t.label}>Nº Lote</label>
                        <input name="numero_lote" value={entrada.numero_lote} onChange={e => setEntrada(prev => ({ ...prev, numero_lote: e.target.value }))} placeholder="Lote..." style={t.input} required />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={t.label}>Quantidade</label>
                        <input value={quantidadeFormatada} onChange={handleQuantidadeChange} placeholder="0" style={t.input} required />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ marginBottom: 14 }}>
                        <label style={t.label}>Peso Embalagem{produtoInfo ? ` (${formatarUnidade(produtoInfo.unidade_peso)})` : ''}</label>
                        <input value={pesoFormatado} onChange={handlePesoChange} placeholder="0,000" style={t.input} required />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={t.label}>Preço de Custo (R$)</label>
                        <input value={precoFormatado} onChange={handlePrecoChange} placeholder="0,00" style={t.input} required />
                    </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={t.label}>Validade</label>
                    <input value={campoValidade} onChange={handleValidadeChange} placeholder="DD/MM/AA" style={{ ...t.input, borderColor: validadeError ? theme.colors.danger : undefined }} required />
                    {validadeError && <p style={{ color: theme.colors.danger, fontSize: 12, marginTop: 4 }}>{validadeError}</p>}
                </div>

                <button type="submit" disabled={salvando} style={{ ...t.btnPrimary, width: '100%', justifyContent: 'center', marginTop: 8, background: theme.colors.success, opacity: salvando ? 0.6 : 1 }}>
                    {salvando ? '⏳ Registrando...' : 'Registrar Entrada'}
                </button>
            </form>
        </>
    );

    if (isModal) {
        return (
            <div style={t.overlay}>
                <div ref={modalRef} style={{ ...t.modal, maxWidth: 480 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: theme.colors.successLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaPlus size={14} color={theme.colors.success} />
                            </div>
                            <div>
                                <h3 style={{ ...t.h3, margin: 0 }}>Entrada de Estoque</h3>
                                {produtoSelecionado && <p style={{ ...t.subtitle, margin: '2px 0 0' }}>{produtoSelecionado.nome}</p>}
                            </div>
                        </div>
                        <button onClick={fecharModal} style={t.btnIcon}><FaTimes size={16} color={theme.colors.textMuted} /></button>
                    </div>
                    {renderFormulario()}
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 520, margin: '16px auto', ...t.card, padding: '24px 32px' }}>
            <h2 style={{ ...t.h1, textAlign: 'center', marginBottom: 20 }}>Entrada de Estoque</h2>
            {renderFormulario()}
        </div>
    );
};

export default StockEntry;
